// Lightweight cookie helpers used as a fallback identity store alongside
// localStorage. The Identity layer mirrors writes to both stores so that:
//   - Returning visitors whose localStorage is cleared (private window
//     promoted to permanent, browser cleanup, etc.) can still re-hydrate
//     from the cookie within the 24h TTL window.
//   - Sub-domain navigation does not strand the handle.
//
// We deliberately avoid any third-party cookie library — these helpers are
// SSR-safe and tree-shakeable.

const ENCODE = encodeURIComponent;
const DECODE = decodeURIComponent;

function isBrowser() {
  return typeof document !== 'undefined' && typeof document.cookie === 'string';
}

function isSecureContext() {
  return (
    typeof window !== 'undefined' &&
    window.location &&
    window.location.protocol === 'https:'
  );
}

/**
 * Write a cookie with a fixed expiry window.
 *
 * Adds `SameSite=Lax` always (good default for first-party identity cookies)
 * and `Secure` only when running on HTTPS — `Secure` on localhost over HTTP
 * would silently drop the cookie.
 *
 * @param {string} name
 * @param {string} value
 * @param {number} days  TTL in days; 0/negative deletes immediately.
 */
export function setCookie(name, value, days = 1) {
  if (!isBrowser()) return;
  if (typeof name !== 'string' || !name) return;
  const maxAgeSeconds = Math.max(0, Math.floor(days * 24 * 60 * 60));
  const parts = [
    `${ENCODE(name)}=${ENCODE(value ?? '')}`,
    `Max-Age=${maxAgeSeconds}`,
    'Path=/',
    'SameSite=Lax',
  ];
  if (isSecureContext()) parts.push('Secure');
  try {
    document.cookie = parts.join('; ');
  } catch {
    /* document.cookie can throw in sandboxed iframes — non-fatal */
  }
}

/**
 * Read a cookie value by name. Returns `null` when the cookie is absent.
 *
 * @param {string} name
 * @returns {string | null}
 */
export function getCookie(name) {
  if (!isBrowser()) return null;
  if (typeof name !== 'string' || !name) return null;
  const target = ENCODE(name);
  const cookies = document.cookie ? document.cookie.split(';') : [];
  for (let i = 0; i < cookies.length; i += 1) {
    const raw = cookies[i].trim();
    if (!raw) continue;
    const eq = raw.indexOf('=');
    const key = eq === -1 ? raw : raw.slice(0, eq);
    if (key !== target) continue;
    const value = eq === -1 ? '' : raw.slice(eq + 1);
    try {
      return DECODE(value);
    } catch {
      return value;
    }
  }
  return null;
}

/**
 * Remove a cookie by setting Max-Age=0. Path must match the write site for
 * the deletion to take effect.
 *
 * @param {string} name
 */
export function deleteCookie(name) {
  if (!isBrowser()) return;
  if (typeof name !== 'string' || !name) return;
  const parts = [
    `${ENCODE(name)}=`,
    'Max-Age=0',
    'Path=/',
    'SameSite=Lax',
  ];
  if (isSecureContext()) parts.push('Secure');
  try {
    document.cookie = parts.join('; ');
  } catch {
    /* non-fatal */
  }
}

// Canonical cookie key names — single source of truth so the provider and
// any future consumer use the exact same strings.
export const COOKIE_KEYS = Object.freeze({
  handle: 'glacier_handle',
  partyCode: 'glacier_party',
});
